import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Flame, Loader2, Users, Sparkles, Plus, Copy, ChevronRight, X, CheckCircle2 } from "lucide-react";

const STATUS = {
  a_venir: { l: "À venir", c: "bg-blue-500/20 text-blue-300" },
  actif: { l: "Actif", c: "bg-emerald-500/20 text-emerald-300" },
  termine: { l: "Terminé", c: "bg-zinc-700 text-zinc-400" },
  annule: { l: "Annulé", c: "bg-red-500/20 text-red-300" },
};
const KINDS = [
  { k: "affiche", l: "Affiche" }, { k: "facebook", l: "Facebook" }, { k: "instagram", l: "Instagram" },
  { k: "tiktok", l: "TikTok" }, { k: "story", l: "Story" }, { k: "courriel", l: "Courriel" },
  { k: "rappel", l: "Rappel quotidien" }, { k: "bilan", l: "Bilan hebdo" }, { k: "certificat", l: "Certificat" },
];

export function DefisKaizenPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/kaizen-challenges"); setItems(data.challenges || []); }
    catch { toast.error("Erreur chargement des défis"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const seedRentree = async () => {
    try {
      const { data } = await api.post("/kaizen-challenges/seed-rentree");
      toast.success(data.already ? "Le défi Rentrée existe déjà" : "✅ Défi Kaizen Rentrée créé (7 sept 2026)");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-4" data-testid="defis-kaizen-panel">
      <div className="rounded-xl border border-orange-800/50 bg-orange-950/10 p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-bold text-orange-200 flex items-center gap-2"><Flame className="w-5 h-5" /> Défis Kaizen & transformations</h3>
          <p className="text-xs text-zinc-400 mt-1">Défis 7 / 21 / 30 / 60 jours, ouverts aux membres et à la communauté. Régularité, énergie et progression — sans promesse médicale.</p>
        </div>
        <button onClick={seedRentree} data-testid="defis-seed-rentree" className="flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white font-semibold px-4 py-2 rounded-lg text-sm">
          <Plus className="w-4 h-4" /> Créer « Défi Rentrée 21 j »
        </button>
      </div>

      {loading ? <Loader2 className="w-6 h-6 animate-spin text-amber-400 mx-auto mt-10" /> :
        items.length === 0 ? (
          <div className="text-center text-zinc-500 py-16" data-testid="defis-empty">Aucun défi. Crée le « Défi Rentrée 21 jours » pour commencer 🔥</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="defis-list">
            {items.map((c) => {
              const st = STATUS[c.status] || STATUS.a_venir;
              return (
                <button key={c.id} onClick={() => setOpenId(c.id)} data-testid={`defi-card-${c.id}`}
                  className="text-left rounded-xl border border-zinc-800 bg-zinc-900/60 hover:border-orange-600/50 p-4 transition-colors">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${st.c}`}>{st.l}</span>
                    <span className="text-[11px] text-zinc-500 flex items-center gap-1"><Users className="w-3.5 h-3.5" />{c.participants_count || 0}</span>
                  </div>
                  <div className="font-bold text-zinc-100">{c.nom}</div>
                  <div className="text-xs text-zinc-500">{c.sous_titre}</div>
                  <div className="text-[11px] text-orange-300/80 mt-2">Départ : {c.date_debut} · {c.duree_jours} jours</div>
                </button>
              );
            })}
          </div>
        )}

      {openId && <DefiDetail id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}

function DefiDetail({ id, onClose, onChanged }) {
  const [c, setC] = useState(null);
  const [content, setContent] = useState(null);
  const [gen, setGen] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/kaizen-challenges/${id}`); setC(data.challenge); }
    catch { toast.error("Erreur"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    try { await api.put(`/kaizen-challenges/${id}`, { status }); toast.success("Statut mis à jour"); await load(); onChanged(); }
    catch { toast.error("Erreur"); }
  };
  const generate = async (kind) => {
    setGen(true);
    try { const { data } = await api.get(`/kaizen-challenges/${id}/content?kind=${kind}`); setContent({ kind, text: data.text }); }
    catch { toast.error("Erreur"); }
    setGen(false);
  };
  const copy = () => { navigator.clipboard?.writeText(content.text); toast.success("Texte copié"); };

  if (!c) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl max-w-2xl w-full max-h-[88vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="defi-detail">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-orange-200">{c.nom}</h2>
            <p className="text-sm text-zinc-400">{c.sous_titre} · départ {c.date_debut}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex items-center gap-3 mt-3 text-sm">
          <span className="text-zinc-300 flex items-center gap-1"><Users className="w-4 h-4" /> {c.participants_count} inscrit(s)</span>
          <span className="text-emerald-300">· {c.actifs} actif(s)</span>
        </div>

        {c.affiche_url && (
          <div className="mt-3" data-testid="defi-affiche">
            <img src={c.affiche_url} alt="Affiche du défi" className="w-40 rounded-lg border border-zinc-700" />
            <a href={c.affiche_url} target="_blank" rel="noreferrer" download
              className="inline-block mt-1 text-xs text-orange-300 hover:underline">⬇ Télécharger / imprimer l'affiche</a>
          </div>
        )}

        <div className="flex gap-2 mt-3 flex-wrap">
          {["a_venir", "actif", "termine", "annule"].map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`defi-status-${s}`}
              className={`text-xs px-3 py-1.5 rounded-full border ${c.status === s ? "bg-orange-600 border-orange-500 text-white" : "bg-zinc-950 border-zinc-700 text-zinc-400"}`}>
              {STATUS[s].l}
            </button>
          ))}
        </div>

        {c.piliers && (
          <div className="mt-4 grid grid-cols-2 gap-2">
            {c.piliers.map((p) => (
              <div key={p.key} className="rounded-lg bg-zinc-950 border border-zinc-800 p-2.5">
                <div className="text-sm font-semibold text-orange-300">{p.nom}</div>
                <div className="text-[11px] text-zinc-500">{p.desc}</div>
              </div>
            ))}
          </div>
        )}

        {c.securite && <p className="mt-3 text-[11px] text-amber-400/80 bg-amber-950/20 border border-amber-900/40 rounded-lg p-2">⚠️ {c.securite}</p>}

        <div className="mt-4">
          <div className="text-sm font-bold text-zinc-200 flex items-center gap-1.5 mb-2"><Sparkles className="w-4 h-4 text-violet-400" /> Générateur de textes (IA-assisté)</div>
          <div className="flex flex-wrap gap-1.5">
            {KINDS.map((k) => (
              <button key={k.k} onClick={() => generate(k.k)} disabled={gen} data-testid={`defi-gen-${k.k}`}
                className="text-xs px-2.5 py-1.5 rounded bg-zinc-800 hover:bg-violet-700 text-zinc-300 disabled:opacity-50">{k.l}</button>
            ))}
          </div>
          {content && (
            <div className="mt-2 rounded-lg bg-zinc-950 border border-zinc-800 p-3" data-testid="defi-content">
              <div className="flex justify-end mb-1">
                <button onClick={copy} className="text-xs text-violet-300 flex items-center gap-1"><Copy className="w-3.5 h-3.5" /> Copier</button>
              </div>
              <pre className="text-xs text-zinc-200 whitespace-pre-wrap font-sans">{content.text}</pre>
            </div>
          )}
        </div>

        {c.jours && c.jours.length > 0 && (
          <details className="mt-4">
            <summary className="text-sm text-zinc-300 cursor-pointer">📅 Calendrier {c.duree_jours} jours</summary>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {c.jours.map((j) => (
                <div key={j.jour} className="text-[11px] text-zinc-400 bg-zinc-950 border border-zinc-800 rounded px-2 py-1">
                  <b className="text-orange-300">J{j.jour}</b> · {j.tache}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}
